import type { TranscriptView } from '../api/client.ts';

/**
 * What a voice bubble knows about its own text.
 *
 * Five states rather than the server's three, because two of the server's `done`
 * mean different things on screen: words to read, and a recording that turned out
 * to have none in it. The second is a result — a pocket, or three seconds of
 * somebody thinking better of it — and offering to «попробовать ещё раз» there
 * would be offering to get the same answer again.
 */
export type TranscriptState = 'none' | 'pending' | 'ready' | 'empty' | 'failed';

export function transcriptState(transcript: TranscriptView | null): TranscriptState {
  if (transcript === null) return 'none';
  if (transcript.status === 'pending') return 'pending';
  if (transcript.status === 'failed') return 'failed';
  return transcript.text === null || transcript.text.trim() === '' ? 'empty' : 'ready';
}

/**
 * The line the block carries when there are no words to put in it. `null` for
 * the two states that have their own content — the text itself, and the nothing
 * that is shown before anybody has asked.
 */
export function transcriptNotice(state: TranscriptState): string | null {
  if (state === 'pending') return 'Распознаём…';
  if (state === 'failed') return 'Не удалось распознать';
  if (state === 'empty') return 'Речь не распознана';
  return null;
}

/**
 * Whether the block under the waveform is shown at all, given what the reader
 * has done to the button.
 *
 * `open` is this device's toggle and starts unset, which is what makes a
 * transcript that already exists visible on arrival: it is the whole point of
 * storing one, and hiding it behind a second press would make a recognised
 * message look exactly like an unrecognised one. Once anybody presses the
 * button, their choice wins.
 */
export function transcriptShown(state: TranscriptState, open: boolean | null): boolean {
  if (state === 'none') return false;
  return open ?? true;
}

/** What the «Аа» button does next, and what a screen reader is told it does. */
export function transcriptAction(
  state: TranscriptState,
  open: boolean | null,
): { intent: 'start' | 'retry' | 'toggle'; label: string; busy: boolean } {
  if (state === 'pending') return { intent: 'toggle', label: 'Распознаём…', busy: true };
  if (state === 'none') return { intent: 'start', label: 'Показать текстом', busy: false };
  if (state === 'failed') return { intent: 'retry', label: 'Распознать ещё раз', busy: false };
  return {
    intent: 'toggle',
    label: transcriptShown(state, open) ? 'Скрыть текст' : 'Показать текстом',
    busy: false,
  };
}
